import React, { useState } from "react";
import { Flex, Grid, Text } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import AppButton from "@/features/line-sheet-sets/app-button";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import PathPickSizeStockModal from "@/features/pathpick/components/product/PathPickSizeStockModal";
import { START_GUIDE_CARD_VIEW } from "@/features/pathpick/features/start-guide/constants";
import PathPickStartGuideSavedProductCard from "@/features/pathpick/features/start-guide/components/PathPickStartGuideSavedProductCard";
import PathPickStartGuideCard from "@/features/pathpick/features/start-guide/components/PathPickStartGuideCard";
import PathPickStartGuideDisabledHint from "@/features/pathpick/features/start-guide/components/PathPickStartGuideDisabledHint";
import PathPickStartGuideStepPanel from "@/features/pathpick/features/start-guide/components/PathPickStartGuideStepPanel";
import PathPickStartGuideActionPanel from "@/features/pathpick/features/start-guide/components/workbench/PathPickStartGuideActionPanel";
import PathPickStartGuideRegistrationExampleModal from "@/features/pathpick/features/start-guide/components/workbench/PathPickStartGuideRegistrationExampleModal";
import {
  DEMO_KREAM_OPTIONS,
  DEMO_POIZON_OPTIONS,
  DEMO_PRODUCTS,
  withDemoRequestState,
} from "@/features/pathpick/features/start-guide/utils/demoProducts";
import { StartGuideDemoState } from "@/features/pathpick/features/start-guide/hooks/useStartGuideDemoState";
import { StartGuideSalesMode } from "@/features/pathpick/features/start-guide/types";
import { PathPickProduct } from "@/features/pathpick/types";

interface Props {
  demo: StartGuideDemoState;
  salesMode: StartGuideSalesMode;
  onToggleSelect: (id: number) => void;
  onRunRegistration: (method: "api" | "file") => void;
  // 등록 예시를 열었다고 목 상태에 남긴다 — 다음 단계 CTA 판정에 쓰인다.
  onViewExample: () => void;
  onPrev: () => void;
  onNext: () => void;
}

const SELECT_HINT_ID = "start-guide-workbench-select-hint";
const NEXT_HINT_ID = "start-guide-workbench-next-hint";

/**
 * 담은 상품 작업대 — 상품 찾기에서 담은 상품으로 등록을 체험하는 화면.
 *
 * 카드는 실제 담은 상품 카드(`PathPickBasketProductGridItem`)를 그대로 쓴다.
 * 여기서 일어나는 일은 전부 목이라 실제 담은 상품·등록 신청 내역에 반영되지 않는다.
 */
function PathPickStartGuideWorkbench({
  demo,
  salesMode,
  onToggleSelect,
  onRunRegistration,
  onViewExample,
  onPrev,
  onNext,
}: Props) {
  const { t } = useI18nHelper();
  const queryClient = useQueryClient();
  const [sizeStockItem, setSizeStockItem] = useState<PathPickProduct | null>(
    null
  );
  const [isExampleOpen, setIsExampleOpen] = useState(false);

  // 담은 상품 목록은 실제로 담은 것만 보여준다.
  const items = DEMO_PRODUCTS.filter((item) =>
    demo.pickedIds.includes(item.id)
  );
  const selectedCount = items.filter((item) =>
    demo.selectedIds.includes(item.id)
  ).length;
  // 담은 상품을 모두 선택해야 작업 버튼이 열린다 — 한쪽만 등록해 보고 넘어가지 않게 한다.
  const isReady = items.length > 0 && selectedCount === items.length;

  // KREAM 을 고른 사용자는 등록 신청 자체를 만들지 않고 주문 안내로 넘어간다.
  const isKreamOnly = salesMode === "kream";

  // KREAM 판매 방식은 등록만이 아니라 주문까지 이 화면에서 체험한다 — 문구가 갈린다.
  const stepPrefix = isKreamOnly
    ? "path_pick_guide:workbench.kream"
    : "path_pick_guide:workbench";

  const openSizeStock = (item: PathPickProduct) => {
    const kreamOptions = DEMO_KREAM_OPTIONS[item.id];
    if (kreamOptions) {
      queryClient.setQueryData(
        ["pathpick", "kream-options", item.id],
        kreamOptions
      );
    }
    const poizonOptions = DEMO_POIZON_OPTIONS[item.id];
    if (poizonOptions) {
      queryClient.setQueryData(
        ["pathpick", "poizon-options", item.id],
        poizonOptions
      );
    }
    setSizeStockItem(item);
  };

  const isDone = demo.registrationStatus === "done";
  // 작업이 시작된 뒤에는 대상이 바뀌면 안 되므로 선택을 잠근다.
  const isSelectionLocked = demo.registrationStatus !== "idle";
  // API 흐름만 등록이 끝난 뒤 스마트스토어에서 무엇을 더 해야 하는지(전시 상태 변경)를
  // 여기서 보고 넘어간다. 엑셀 흐름은 파일을 실제로 받는 자리(등록 신청 내역)에서 같은 안내를 본다.
  const hasViewedExample = demo.hasViewedRegistrationExample;
  const needsExample = salesMode === "api";
  // KREAM 흐름은 목 처리 없이 바로 다음 단계로 간다.
  const canGoNext = isKreamOnly
    ? isReady
    : isDone && (!needsExample || hasViewedExample);

  return (
    <>
      <PathPickStartGuideCard
        header={
          <Flex flexDirection={"column"} gap={"6px"}>
            <Text
              textStyle={"body-lg-bold"}
              fontSize={"22px"}
              color={"#1D1D1B"}
            >
              {t("path_pick_guide:workbench.title")}
            </Text>
            <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
              {t("path_pick_guide:workbench.description")}
            </Text>
          </Flex>
        }
        headerProps={{
          paddingBottom: "0px",
        }}
      >
        <Flex flexDirection={"column"} gap={"16px"}>
          {/* 탭 안의 단계가 하나뿐이라 단계 표시는 두지 않는다.
              앞뒤 이동이 모두 탭 밖으로 나가므로 두 버튼 다 `단계` 라벨이다. */}
          <PathPickStartGuideStepPanel
            title={t(`${stepPrefix}.step_title`)}
            description={t(`${stepPrefix}.step_description`)}
            actions={
              <Flex
                flexDirection={"column"}
                gap={"6px"}
                alignItems={"flex-end"}
              >
                <Flex gap={"8px"}>
                  <AppButton variant={"normal"} onClick={onPrev}>
                    {t("path_pick_guide:nav.prev_step")}
                  </AppButton>
                  <AppButton
                    variant={"primary"}
                    isDisabled={!canGoNext}
                    onClick={onNext}
                    chakraProps={
                      canGoNext
                        ? undefined
                        : { "aria-describedby": NEXT_HINT_ID }
                    }
                  >
                    {t("path_pick_guide:nav.next_step")}
                  </AppButton>
                </Flex>
                {!canGoNext && (
                  <PathPickStartGuideDisabledHint
                    id={NEXT_HINT_ID}
                    textAlign={"right"}
                  >
                    {/* KREAM 흐름은 패널에 실행 버튼이 없어 선택 안내를 여기서 한다.
                        스마트스토어는 작업 완료 → 등록 예시 확인 순서라 남은 쪽을 짚어 준다. */}
                    {t(
                      isKreamOnly
                        ? "path_pick_guide:workbench.select_hint"
                        : isDone && needsExample
                        ? "path_pick_guide:workbench.example_disabled_hint"
                        : "path_pick_guide:workbench.next_disabled_hint"
                    )}
                  </PathPickStartGuideDisabledHint>
                )}
              </Flex>
            }
          />

          {/* lg 이상은 카드 왼쪽 / 작업 패널 오른쪽, 그 아래는 세로로 쌓는다(전달서 §21). */}
          <Grid
            templateColumns={{ base: "1fr", lg: "minmax(0, 1fr) 320px" }}
            gap={"16px"}
            alignItems={"start"}
          >
            <Grid
              templateColumns={"repeat(auto-fill, minmax(200px, 240px))"}
              gap={"6px"}
            >
              {items.map((item) => (
                <PathPickStartGuideSavedProductCard
                  key={item.id}
                  // 등록을 마친 상품은 운영 카드와 같은 완료 배지를 달고 나온다.
                  item={withDemoRequestState(
                    item,
                    demo.productStateById[item.id]
                  )}
                  isSelected={demo.selectedIds.includes(item.id)}
                  isSelectionLocked={isSelectionLocked}
                  onToggleSelect={() => onToggleSelect(item.id)}
                  onOpenSizeStock={() => openSizeStock(item)}
                />
              ))}
            </Grid>

            <Flex flexDirection={"column"} gap={"8px"}>
              <PathPickStartGuideActionPanel
                variant={salesMode}
                icon={
                  salesMode === "api"
                    ? "upload-cloud"
                    : salesMode === "file"
                    ? "download"
                    : "cart"
                }
                // KREAM 흐름에는 목 처리 단계가 없어 항상 idle 이다.
                status={isKreamOnly ? "idle" : demo.registrationStatus}
                isDisabled={!isReady}
                disabledHintId={SELECT_HINT_ID}
                // 완료 문구가 실제 처리한 상품 수를 말하도록 넘긴다.
                selectedCount={selectedCount}
                // 사용량은 완료된 상품만 차감된다 — KREAM 흐름은 등록 자체가 없어 해당 없다.
                doneDescription={
                  isKreamOnly
                    ? undefined
                    : t("path_pick_guide:workbench.done_usage_notice", {
                        selected: selectedCount,
                      })
                }
                // 작업 대상 확인용 선택 수. 사용량 숫자는 전달서 §10 이 금지한다.
                meta={
                  <Flex
                    justifyContent={"space-between"}
                    alignItems={"baseline"}
                    gap={"6px"}
                  >
                    <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
                      {t("path_pick_guide:workbench.selected_label")}
                    </Text>
                    <Text
                      textStyle={"body-sm-bold"}
                      color={"#1272EF"}
                      role={"status"}
                    >
                      {t("path_pick_guide:workbench.selected_count", {
                        selected: selectedCount,
                        total: items.length,
                      })}
                    </Text>
                  </Flex>
                }
                // KREAM 흐름은 실행할 목 작업이 없어 패널이 설명만 남는다 —
                // 다음 단계 이동은 화면 상단의 `주문 안내 보기` 하나로 모은다.
                hasAction={!isKreamOnly}
                // 등록 방식 값이 판매 방식과 같아 그대로 넘긴다(KREAM 은 위에서 갈렸다).
                onRun={
                  isKreamOnly ? onNext : () => onRunRegistration(salesMode)
                }
                footer={
                  needsExample && isDone ? (
                    <AppButton
                      variant={"white"}
                      width={"100%"}
                      onClick={() => {
                        setIsExampleOpen(true);
                        onViewExample();
                      }}
                    >
                      {t("path_pick_guide:workbench.api.example_cta")}
                    </AppButton>
                  ) : undefined
                }
              />
              {!isKreamOnly && !isReady && (
                <PathPickStartGuideDisabledHint id={SELECT_HINT_ID}>
                  {t("path_pick_guide:workbench.select_hint")}
                </PathPickStartGuideDisabledHint>
              )}
            </Flex>
          </Grid>
        </Flex>
      </PathPickStartGuideCard>

      {sizeStockItem && (
        <PathPickSizeStockModal
          isOpen={true}
          onClose={() => setSizeStockItem(null)}
          item={sizeStockItem}
          viewOverride={START_GUIDE_CARD_VIEW}
        />
      )}
      <PathPickStartGuideRegistrationExampleModal
        isOpen={isExampleOpen}
        onClose={() => setIsExampleOpen(false)}
        onNext={onNext}
      />
    </>
  );
}

export default PathPickStartGuideWorkbench;
